import { useState, useCallback, useEffect, useRef } from 'react';
import { Head, Link, router } from '@inertiajs/react';
import { AppShell } from '../../../components/layout';
import { Card } from '../../../components/ui';
import {
  WordCard,
  WordSearch,
  FrequencyBadge,
  TierBadge,
  PosBadge,
} from '../../../components/dictionary';
import { Check, ChevronDown, ChevronRight, Plus } from 'lucide-react';
import { cn } from '../../../lib/cn';
import { useBreadcrumbs } from '../../../hooks/useBreadcrumbs';

const TABS = [
  { key: 'assigned', label: 'Assigned' },
  { key: 'personal', label: 'My Words' },
];

const STATE_DOTS = {
  new: 'bg-gray-400',
  learning: 'bg-yellow-400',
  review: 'bg-green-400',
  relearning: 'bg-orange-400',
};

const FREQUENCY_COLORS = {
  core: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  common: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  academic: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
  literary: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  rare: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
};

// Drives the color of the thin per-card progress bar. Matches the
// grammar-profile convention on the teacher user page.
const BUCKET_BAR_COLORS = {
  mastered: 'bg-green-500',
  learning: 'bg-amber-500',
  not_started: 'bg-gray-400',
};

// Assigned-tab pills: tint the pill's border + background by mastery bucket
// so the status is legible at a glance without an extra inline label.
const BUCKET_PILL_STYLES = {
  mastered:
    'border-green-300 bg-green-50 text-green-900 hover:bg-green-100 dark:border-green-800 dark:bg-green-900/20 dark:text-green-100 dark:hover:bg-green-900/30',
  learning:
    'border-amber-300 bg-amber-50 text-amber-900 hover:bg-amber-100 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-100 dark:hover:bg-amber-900/30',
  not_started:
    'border-gray-200 bg-white text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800/40 dark:text-gray-200 dark:hover:bg-gray-800',
};

// Ordered so the most-actionable buckets float to the top of a group.
const BUCKET_ORDER = ['learning', 'not_started', 'mastered'];
const BUCKET_SUBHEADINGS = {
  learning: 'In progress',
  not_started: 'Not started',
  mastered: 'Mastered',
};

export default function StudentDictionaryIndex({
  student,
  due_count,
  pending_onboarding_count,
  next_action,
  can_choose_path = false,
  completed_onboardings_count = 0,
  paused_review_session,
  resumable_onboarding_session,
  personal_cards,
  assigned_groups = [],
  featuredWords,
  stats,
  frequencyBands,
  beckTiers,
}) {
  const { clear: clearBreadcrumbs } = useBreadcrumbs();
  useEffect(() => {
    // Section index is a dead-end relative to the vertical-nav axis; clear any
    // stale trail (e.g. book/chapter from a prior ChapterReader visit).
    clearBreadcrumbs();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const [activeTab, setActiveTab] = useState('assigned');
  // Assigned words group by mastery bucket by default; 'source' restores the
  // by-book / by-list / by-course grouping the server sends.
  const [assignedGrouping, setAssignedGrouping] = useState('progress');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  // Default: every group expanded on first render; user-collapsed state
  // persists across tab switches within the page load.
  const [collapsedGroups, setCollapsedGroups] = useState(() => new Set());
  const toggleGroup = (key) =>
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });

  // Ref for the dictionary section (search input + results). Used to scroll
  // into view when a search is active so results are visible without manual
  // scrolling past the hero and word-card lists.
  const dictSectionRef = useRef(null);
  // Tracks the AbortController for the current in-flight search fetch so we
  // can cancel it when a newer search starts, avoiding stale-result races.
  const abortRef = useRef(null);

  const assignedCardCount = assigned_groups.reduce(
    (sum, g) => sum + (g.count ?? g.cards?.length ?? 0),
    0
  );

  // Flattened assigned cards, each tagged with the label of the source group
  // it came from so the progress view can still surface provenance on hover.
  const assignedCards = assigned_groups.flatMap((g) =>
    (g.cards ?? []).map((card) => ({ ...card, source_label: g.label }))
  );

  // Progress view (the default): one group per mastery bucket, pooled across
  // every book / list / course the words came from.
  const assignedByProgress = BUCKET_ORDER.map((bucket) => {
    const cards = assignedCards.filter(
      (c) => (c.mastery_bucket || 'not_started') === bucket
    );
    return { key: `bucket:${bucket}`, label: BUCKET_SUBHEADINGS[bucket], count: cards.length, cards };
  }).filter((g) => g.count > 0);

  const visibleAssignedGroups =
    assignedGrouping === 'progress' ? assignedByProgress : assigned_groups;

  const csrfToken =
    typeof document !== 'undefined'
      ? document.querySelector('meta[name="csrf-token"]')?.content
      : '';

  const startSession = (endpoint) => {
    fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken,
        Accept: 'application/json',
      },
    })
      .then((r) => r.json())
      .then((data) => {
        if (data.session_id) {
          const path = endpoint.endsWith('onboarding_sessions')
            ? `/student/vocabulary/onboarding_sessions/${data.session_id}`
            : `/student/vocabulary/review_sessions/${data.session_id}`;
          router.visit(path);
        }
      });
  };

  // Per-path handlers — used by both the single-CTA hero (via handleStudyNow
  // dispatching on next_action) and the dual-CTA hero (each button wires its
  // own path directly).
  const handleOnboardingPath = () => {
    if (resumable_onboarding_session) {
      // §3.7 — onboarding session state is persisted on every submit; just
      // visit /show to land back on the current stage's payload.
      router.visit(`/student/vocabulary/onboarding_sessions/${resumable_onboarding_session.id}`);
    } else if (pending_onboarding_count > 0) {
      // Send the student to the list picker first.
      router.visit('/student/vocabulary/onboarding_sessions/configure');
    }
  };

  const handleReviewPath = () => {
    if (paused_review_session) {
      // Show action clears paused_at on visit; no extra POST needed.
      router.visit(`/student/vocabulary/review_sessions/${paused_review_session.id}`);
    } else if (due_count > 0) {
      startSession('/student/vocabulary/review_sessions');
    }
  };

  const handleStudyNow = () => {
    if (next_action === 'resume' || next_action === 'review') return handleReviewPath();
    if (next_action === 'resume_onboarding' || next_action === 'onboard') return handleOnboardingPath();
  };

  const handleSearch = useCallback(async (query) => {
    const trimmed = query.trim();

    // Keep the URL in sync so the search is bookmarkable / survives refresh.
    // Skip replaceState when the URL already carries the correct param to
    // avoid a redundant history entry on the initial mount useEffect call.
    const url = new URL(window.location.href);
    if (trimmed) {
      url.searchParams.set('q', trimmed);
    } else {
      url.searchParams.delete('q');
    }
    const currentQ = new URLSearchParams(window.location.search).get('q') ?? '';
    if (currentQ !== trimmed) {
      window.history.replaceState(null, '', url);
    }

    if (!trimmed) {
      abortRef.current?.abort();
      setIsSearching(false);
      setSearchResults([]);
      setHasSearched(false);
      return;
    }

    // Cancel the previous in-flight fetch so stale results don't overwrite
    // a newer search when the user types quickly.
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setIsSearching(true);
    setHasSearched(true);
    try {
      const response = await fetch(
        `/student/dictionary/search?q=${encodeURIComponent(trimmed)}`,
        { signal: controller.signal }
      );
      const data = await response.json();
      if (data.success) {
        setSearchResults(data.data);
      } else {
        setSearchResults([]);
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Search error:', error);
        setSearchResults([]);
      }
    } finally {
      if (!controller.signal.aborted) {
        setIsSearching(false);
      }
    }
  }, []);

  // On mount: if the URL contains ?q=, pre-populate the search field and
  // fire the search automatically so results are visible immediately.
  useEffect(() => {
    const q = new URLSearchParams(window.location.search).get('q');
    if (q) {
      setSearchQuery(q);
      handleSearch(q);
    }
  }, [handleSearch]);

  // Must be declared before the scroll effect so the dependency array
  // doesn't reference it in the Temporal Dead Zone.
  const isSearchActive = hasSearched && searchQuery.trim().length > 0;

  // Scroll the dictionary section into view when a search becomes active so
  // results don't require manual scrolling past the hero and card lists.
  useEffect(() => {
    if (isSearchActive && dictSectionRef.current) {
      requestAnimationFrame(() => {
        dictSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }
  }, [isSearchActive]);

  const personalEmpty = personal_cards.length === 0;
  const assignedEmpty = assignedCardCount === 0;

  // Per-path CTA descriptors. Either may be null if that path has nothing
  // to do. The choice-mode hero renders both side-by-side when both are
  // non-null and `can_choose_path` is true. Single-mode picks one based on
  // next_action (the server's recommended ordering).
  const onboardingCta = (() => {
    if (resumable_onboarding_session) {
      const { total, current_card_index, stage_label } = resumable_onboarding_session;
      const cardPos = Math.min(current_card_index + 1, total);
      return {
        label: `Resume onboarding (${cardPos}/${total})`,
        sub: `${stage_label} — word ${cardPos} of ${total}.`,
      };
    }
    if (pending_onboarding_count > 0) {
      return {
        label: 'Start onboarding',
        sub: `${pending_onboarding_count} new ${pending_onboarding_count === 1 ? 'word' : 'words'} to learn.`,
      };
    }
    return null;
  })();

  const reviewCta = (() => {
    if (paused_review_session) {
      const { answered, total } = paused_review_session;
      return {
        label: `Resume practice (${answered}/${total})`,
        sub: `${answered} of ${total} done in your last session.`,
      };
    }
    if (due_count > 0) {
      return {
        label: 'Practice now',
        sub: `${due_count} ${due_count === 1 ? 'word' : 'words'} due for review.`,
      };
    }
    return null;
  })();

  const showDualCta = can_choose_path && !!onboardingCta && !!reviewCta;

  // Single-mode hero copy. Falls back to "caught up" when neither path
  // has work. The dual-mode hero (rendered below) doesn't use this.
  const singleHero = (() => {
    if (next_action === 'resume' && reviewCta) {
      return { headline: 'Pick up where you left off', sub: reviewCta.sub, cta: reviewCta.label };
    }
    if (next_action === 'resume_onboarding' && onboardingCta) {
      return { headline: 'Pick up where you left off', sub: onboardingCta.sub, cta: onboardingCta.label };
    }
    if (next_action === 'onboard' && onboardingCta) {
      return {
        headline: `You have ${pending_onboarding_count} new ${pending_onboarding_count === 1 ? 'word' : 'words'} to learn`,
        sub: 'Get to know them before they enter your review queue.',
        cta: 'Start learning',
      };
    }
    if (next_action === 'review' && reviewCta) {
      return {
        headline: `You have ${due_count} ${due_count === 1 ? 'word' : 'words'} due for review`,
        sub: 'Practice makes permanent.',
        cta: 'Study now',
      };
    }
    return { headline: "You're caught up", sub: 'Nothing due right now.', cta: null };
  })();

  return (
    <AppShell student={student} scrollTitle="Dictionary">
      <Head title="Dictionary" />

      <div className="max-w-5xl mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">
          My Vocabulary
        </h1>

        {/* Top — Hero / Study Now */}
        <Card className="mb-8 bg-gradient-to-r from-gray-50 to-white dark:from-gray-800 dark:to-gray-900 border border-gray-200 dark:border-gray-700">
          {showDualCta ? (
            // Choice mode (unlocked after ONBOARDING_PRACTICE_CHOICE_THRESHOLD
            // completed onboardings — see DictionaryController). Both paths
            // have work waiting; let the student pick.
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div>
                <p className="font-medium text-gray-900 dark:text-white">
                  What sounds good today?
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                  {onboardingCta.sub} · {reviewCta.sub}
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={handleOnboardingPath}
                  className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                >
                  {onboardingCta.label}
                </button>
                <button
                  onClick={handleReviewPath}
                  className="glow-button px-4 py-2 text-sm font-medium rounded-lg bg-gray-900 text-white hover:bg-gray-800 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-200 transition-colors"
                >
                  {reviewCta.label}
                </button>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-gray-900 dark:text-white">
                  {singleHero.headline}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                  {singleHero.sub}
                </p>
              </div>
              {singleHero.cta && (
                <button
                  onClick={handleStudyNow}
                  className="glow-button px-4 py-2 text-sm font-medium rounded-lg bg-gray-900 text-white hover:bg-gray-800 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-200 transition-colors"
                >
                  {singleHero.cta}
                </button>
              )}
            </div>
          )}
        </Card>

        {/* Middle — My Words / Assigned tabs */}
        <div className="flex gap-1 mb-4 border-b border-gray-200 dark:border-gray-700">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={cn(
                'px-4 py-2 text-sm font-medium border-b-2 transition-colors -mb-px',
                activeTab === tab.key
                  ? 'border-gray-900 dark:border-white text-gray-900 dark:text-white'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              )}
            >
              {tab.label}
              <span className="ml-1.5 text-xs text-gray-400">
                {tab.key === 'personal' ? personal_cards.length : assignedCardCount}
              </span>
            </button>
          ))}
        </div>

        {activeTab === 'personal' ? (
          personalEmpty ? (
            <Card className="p-8 text-center text-gray-400 dark:text-gray-500 mb-12">
              No words yet. Add words by selecting text in any reading or by searching below.
            </Card>
          ) : (
            <div className="space-y-2 mb-12">
              {personal_cards.map((card) => (
                <CardRow key={card.id} card={card} showSourceChip={false} />
              ))}
            </div>
          )
        ) : assignedEmpty ? (
          <Card className="p-8 text-center text-gray-400 dark:text-gray-500 mb-12">
            No assigned words yet.
          </Card>
        ) : (
          <div className="mb-12">
            <GroupingToggle value={assignedGrouping} onChange={setAssignedGrouping} />
            <div className="space-y-4">
              {visibleAssignedGroups.map((group) => {
                const collapsed = collapsedGroups.has(group.key);
                return (
                  <div key={group.key}>
                    <button
                      type="button"
                      onClick={() => toggleGroup(group.key)}
                      className="w-full flex items-center gap-1.5 py-2 text-left text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
                    >
                      {collapsed ? (
                        <ChevronRight className="w-4 h-4 text-gray-400" />
                      ) : (
                        <ChevronDown className="w-4 h-4 text-gray-400" />
                      )}
                      <span>{group.label}</span>
                      <span className="text-xs text-gray-400 dark:text-gray-500 font-normal">
                        ({group.count})
                      </span>
                    </button>
                    {!collapsed && (
                      <div className="pl-5 pt-1 pb-3">
                        {assignedGrouping === 'progress' ? (
                          // The group heading already *is* the bucket, so pills
                          // render flat and carry their source in the tooltip.
                          <div className="flex flex-wrap gap-1.5">
                            {group.cards.map((card) => (
                              <WordPill key={card.id} card={card} />
                            ))}
                          </div>
                        ) : (
                          <PillsByBucket cards={group.cards} />
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Bottom — Dictionary search & browse */}
        <div ref={dictSectionRef} className="border-t border-gray-200 dark:border-gray-700 pt-10">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            Dictionary
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            Explore {stats.totalWords.toLocaleString()} words with definitions,
            morphology, and word families.
          </p>

          {/* Pass results=[] and isLoading=false so WordSearch's own dropdown
              stays dormant; we render the results inline below to avoid the
              teacher-only link target and give students an Add button. */}
          <WordSearch
            value={searchQuery}
            onChange={setSearchQuery}
            onSearch={handleSearch}
            results={[]}
            isLoading={false}
            inputTestId="dictionary-search-input"
            className="mb-6"
          />

          {/* Search Results — always rendered when there's a query so the
              page doesn't jump as content appears/disappears. */}
          {isSearchActive && (
            <section className="mb-12">
              {isSearching && searchResults.length === 0 ? (
                <div className="py-8 text-center text-gray-500 dark:text-gray-400 text-sm">
                  Searching&hellip;
                </div>
              ) : searchResults.length === 0 ? (
                <div className="py-8 text-center">
                  <p className="text-gray-500 dark:text-gray-400">
                    No words found matching &ldquo;{searchQuery}&rdquo;
                  </p>
                  <p className="text-sm text-gray-400 dark:text-gray-500 mt-2">
                    Try a different spelling.
                  </p>
                </div>
              ) : (
                <>
                  <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-3 uppercase tracking-wide">
                    Search Results
                    <span className="text-gray-400 dark:text-gray-500 font-normal ml-2 normal-case tracking-normal">
                      ({searchResults.length})
                    </span>
                  </h3>
                  <div className="space-y-2">
                    {searchResults.map((word) => (
                      <SearchResultRow
                        key={word.id}
                        word={word}
                        csrfToken={csrfToken}
                      />
                    ))}
                  </div>
                </>
              )}
            </section>
          )}

          {/* Browse content is ALWAYS rendered so the page doesn't collapse
              while typing. */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
            <StatCard label="Words" value={stats.totalWords} />
            <StatCard label="Definitions" value={stats.totalDefinitions} />
            <StatCard label="Morphemes" value={stats.totalMorphemes} />
            <StatCard label="Word Families" value={stats.totalFamilies} />
          </div>

          <section>
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
              Rare Words
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Uncommon vocabulary worth discovering.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {featuredWords.map((word) => (
                <WordCard key={word.id} word={word} variant="card" />
              ))}
            </div>
          </section>

          <section className="mt-12">
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
              Browse by Category
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card className="p-6">
                <h4 className="font-semibold text-gray-900 dark:text-white mb-3">
                  By Frequency Band
                </h4>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  Words grouped by how commonly they appear in English.
                </p>
                <div className="flex flex-wrap gap-2">
                  {frequencyBands.map((band) => (
                    <FrequencyBadge key={band} band={band} size="md" />
                  ))}
                </div>
              </Card>

              <Card className="p-6">
                <h4 className="font-semibold text-gray-900 dark:text-white mb-3">
                  By Beck Tier
                </h4>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  Words categorized by instructional importance.
                </p>
                <div className="flex flex-wrap gap-2">
                  {beckTiers.map((tier) => (
                    <TierBadge key={tier} tier={tier} size="md" showTooltip />
                  ))}
                </div>
              </Card>
            </div>
          </section>
        </div>
      </div>
    </AppShell>
  );
}

// A single word card row with a thin mastery progress bar. Used by both
// "My Words" and each collapsible "Assigned" group.
function CardRow({ card, showSourceChip = true }) {
  const bucket = card.mastery_bucket || 'not_started';
  const pct = card.progress_percent ?? 0;

  return (
    <Link href={`/student/dictionary/${card.word_id}`} className="block group">
      <Card className="px-4 py-3 transition-colors group-hover:border-gray-300 dark:group-hover:border-gray-600 group-hover:bg-gray-50/50 dark:group-hover:bg-gray-800/40">
        <div className="flex items-center gap-3">
        <span
          className={cn(
            'w-2 h-2 rounded-full flex-shrink-0',
            STATE_DOTS[card.fsrs_state] || 'bg-gray-400'
          )}
          title={card.fsrs_state}
        />

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-gray-900 dark:text-white">
              {card.lemma}
            </span>
            {card.pos && (
              <span className="text-xs text-gray-400 dark:text-gray-500">
                {card.pos}
              </span>
            )}
            {card.frequency_band && (
              <span
                className={cn(
                  'text-xs px-1.5 py-0.5 rounded',
                  FREQUENCY_COLORS[card.frequency_band] || ''
                )}
              >
                {card.frequency_band}
              </span>
            )}
            {showSourceChip &&
              card.source_type &&
              card.source_type !== 'self_added' && (
                <span className="text-xs text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded">
                  {card.source_type === 'list_assigned' ? 'List' : 'Teacher'}
                </span>
              )}
          </div>
          {card.context_sentence && (
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 line-clamp-2">
              {card.context_sentence}
            </p>
          )}
        </div>

        <div
          className="flex items-center gap-2 shrink-0"
          title={`${bucket.replace('_', ' ')} · ${card.reps ?? 0} review${card.reps === 1 ? '' : 's'}`}
        >
          <div className="w-16 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
            <div
              className={cn(
                'h-full rounded-full transition-all',
                BUCKET_BAR_COLORS[bucket]
              )}
              style={{ width: `${pct}%` }}
            />
          </div>
          <span className="text-xs text-gray-500 dark:text-gray-400 w-8 text-right tabular-nums">
            {pct}%
          </span>
          <span className="text-xs text-gray-400 dark:text-gray-500 tabular-nums">
            ({card.reps ?? 0})
          </span>
          </div>
        </div>
      </Card>
    </Link>
  );
}

// Segmented control for the Assigned tab. "Progress" pools every assigned
// word into mastery buckets; "Source" restores the per-book / per-list /
// per-course grouping the controller sends.
const GROUPINGS = [
  { key: 'progress', label: 'By progress' },
  { key: 'source', label: 'By source' },
];

function GroupingToggle({ value, onChange }) {
  return (
    <div className="flex items-center justify-end mb-2">
      <div className="inline-flex rounded-lg border border-gray-200 dark:border-gray-700 p-0.5 bg-gray-50 dark:bg-gray-800/50">
        {GROUPINGS.map((g) => (
          <button
            key={g.key}
            type="button"
            onClick={() => onChange(g.key)}
            aria-pressed={value === g.key}
            className={cn(
              'px-2.5 py-1 text-xs font-medium rounded-md transition-colors',
              value === g.key
                ? 'bg-white dark:bg-gray-900 text-gray-900 dark:text-white shadow-sm'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
            )}
          >
            {g.label}
          </button>
        ))}
      </div>
    </div>
  );
}

// Compact pills for the Assigned tab — grouped by mastery bucket. The
// outer collapsible source group (AWL / Course list / etc.) already tells
// the student *where* these words came from, so pills don't need a source
// chip — just the word, POS, and a color that encodes progress.
function PillsByBucket({ cards }) {
  const grouped = BUCKET_ORDER
    .map((bucket) => ({
      bucket,
      items: cards.filter((c) => (c.mastery_bucket || 'not_started') === bucket),
    }))
    .filter((g) => g.items.length > 0);

  return (
    <div className="space-y-3">
      {grouped.map(({ bucket, items }) => (
        <div key={bucket}>
          <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">
            {BUCKET_SUBHEADINGS[bucket]}
            <span className="ml-1 text-gray-400 dark:text-gray-500 font-normal">
              ({items.length})
            </span>
          </p>
          <div className="flex flex-wrap gap-1.5">
            {items.map((card) => (
              <WordPill key={card.id} card={card} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function WordPill({ card }) {
  const bucket = card.mastery_bucket || 'not_started';
  return (
    <Link
      href={`/student/dictionary/${card.word_id}`}
      title={[
        `${card.lemma}${card.pos ? ` (${card.pos})` : ''}`,
        BUCKET_SUBHEADINGS[bucket],
        card.source_label,
      ]
        .filter(Boolean)
        .join(' · ')}
      className={cn(
        'inline-flex items-baseline gap-1.5 px-2.5 py-1 rounded-full border text-sm transition-colors',
        BUCKET_PILL_STYLES[bucket]
      )}
    >
      <span className="font-medium">{card.lemma}</span>
      {card.pos && (
        <span className="text-xs opacity-60">{card.pos}</span>
      )}
    </Link>
  );
}

function SearchResultRow({ word, csrfToken }) {
  // Local status drives the inline Add button only. `alreadyAdded` reflects
  // either the server-supplied flag for words already in this student's deck,
  // or a successful Add we just made on this page.
  const [alreadyAdded, setAlreadyAdded] = useState(Boolean(word.added));
  const [status, setStatus] = useState('idle'); // idle | loading | error
  const [errorMsg, setErrorMsg] = useState(null);

  const handleAdd = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setStatus('loading');
    setErrorMsg(null);
    try {
      const res = await fetch('/student/vocabulary/words', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
          Accept: 'application/json',
        },
        body: JSON.stringify({ surface_form: word.lemma }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setAlreadyAdded(true);
        setStatus('idle');
      } else {
        setStatus('error');
        setErrorMsg(data.error || 'Could not add word');
      }
    } catch {
      setStatus('error');
      setErrorMsg('Network error');
    }
  };

  return (
    <Link
      href={`/student/dictionary/${word.id}`}
      className="block group"
    >
      <Card className="p-4 transition-colors group-hover:border-gray-300 dark:group-hover:border-gray-600 group-hover:bg-gray-50/50 dark:group-hover:bg-gray-800/40">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-lg font-semibold text-gray-900 dark:text-white group-hover:underline underline-offset-2">
                {word.lemma}
              </span>
              <PosBadge pos={word.pos} size="sm" />
              <FrequencyBadge band={word.frequencyBand} size="sm" />
            </div>
            {word.primaryDefinition && (
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 line-clamp-2">
                {word.primaryDefinition}
              </p>
            )}
          </div>

          <div className="flex-shrink-0" onClick={(e) => e.stopPropagation()}>
            {alreadyAdded ? (
              <span
                className="inline-flex items-center gap-1 text-sm text-green-700 dark:text-green-400 px-3 py-1.5 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-900/40"
                title="Already in your words"
              >
                <Check className="w-4 h-4" />
                In your words
              </span>
            ) : status === 'error' ? (
              <button
                onClick={handleAdd}
                className="text-sm text-red-500 hover:text-red-600 px-3 py-1.5"
                title={errorMsg || 'Retry'}
              >
                Retry
              </button>
            ) : (
              <button
                onClick={handleAdd}
                disabled={status === 'loading'}
                className={cn(
                  'inline-flex items-center gap-1 text-sm font-medium px-3 py-1.5 rounded-lg',
                  'border border-gray-200 dark:border-gray-700',
                  'text-gray-700 dark:text-gray-300',
                  'hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors',
                  status === 'loading' && 'opacity-50 cursor-not-allowed'
                )}
              >
                <Plus className="w-4 h-4" />
                {status === 'loading' ? 'Adding…' : 'Add'}
              </button>
            )}
          </div>
        </div>
      </Card>
    </Link>
  );
}

function StatCard({ label, value }) {
  return (
    <Card className="p-4 text-center">
      <div className="text-2xl font-bold text-gray-900 dark:text-white">
        {typeof value === 'number' ? value.toLocaleString() : value}
      </div>
      <div className="text-sm text-gray-600 dark:text-gray-400">{label}</div>
    </Card>
  );
}
